/* global gsap, ScrollTrigger */

class ProjectsSlider {
  constructor() {
    this.slider = null;
    this.currentPosition = 0;
    this.targetPosition = 0; // For smooth motion
    this.lerpEase = 0.12; // Base smoothing factor
    this.scrollSpeed = 0.5; // Scroll speed multiplier
    this.maxScroll = 0;
    this.isDragging = false;
    this.hasBeenDragged = false;
    this.isInViewport = false;
    this.lastScrollY = window.scrollY;
    this.isInitialized = false;
    this.isScrolling = false; // Detect active scroll
    this.scrollStopTimeout = null;
    this.lastScrollTime = 0; // Track last scroll event time

    this.init();
  }

  init() {
    // Desktop drag/scroll disabled: Slick slider is used on all viewports
    return;

    if (window.innerWidth < 1200) return;

    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", () => this.setup())
      : this.setup();
  }

  setup() {
    if (window.innerWidth < 1200) return;

    this.slider = document.querySelector(".wrg.projects-slider");
    if (!this.slider) return;

    const slides = this.slider.querySelectorAll(".projects-slider__slide");
    if (!slides.length) return;

    // Hardware acceleration for smoother motion
    this.slider.style.willChange = "transform";
    this.slider.style.backfaceVisibility = "hidden";
    this.slider.style.perspective = "1000px";
    this.slider.style.transform = "translateZ(0)";
    this.slider.style.isolation = "isolate";
    this.slider.style.cursor = "grab";

    this.createInfiniteLoop(slides);
    gsap.registerPlugin(ScrollTrigger);

    this.calculateDimensions();
    this.setupEvents();
    this.setupDrag();
    this.setupResize();

    // Start the smooth update loop
    gsap.ticker.add(this.update.bind(this));

    this.isInitialized = true;
  }

  createInfiniteLoop(originalSlides) {
    const slidesArray = Array.from(originalSlides);
    for (let i = 0; i < 5; i++) {
      slidesArray.forEach((slide) => {
        const clone = slide.cloneNode(true);
        this.slider.appendChild(clone);
      });
    }
    this.originalSlideCount = slidesArray.length;
    this.totalSlides = this.originalSlideCount * 6;

    // Start at middle set for seamless infinite feel
    this.currentPosition =
      -this.originalSlideCount *
      this.slider.querySelector(".projects-slider__slide").offsetWidth *
      2;
    this.targetPosition = this.currentPosition;
    gsap.set(this.slider, { x: this.currentPosition });
  }

  calculateDimensions() {
    const slide = this.slider.querySelector(".projects-slider__slide");
    if (!slide) return;

    const gap = 40; // Desktop only
    const slideWidth = slide.offsetWidth;
    const singleSetWidth =
      slideWidth * this.originalSlideCount +
      gap * (this.originalSlideCount - 1);
    const totalWidth = singleSetWidth * 6;
    const containerWidth = this.slider.parentElement.offsetWidth;

    this.singleSetWidth = singleSetWidth;
    this.totalWidth = totalWidth;
    this.maxScroll = Math.max(0, totalWidth - containerWidth);
  }

  setupEvents() {
    const section = this.slider.closest(".section--projects-slider");
    if (!section) return;

    ScrollTrigger.create({
      trigger: section,
      start: "top bottom",
      end: "bottom top",
      onToggle: (self) => (this.isInViewport = self.isActive),
    });

    this.scrollHandler = () => {
      const scrollDelta = window.scrollY - this.lastScrollY;
      this.lastScrollY = window.scrollY;
      this.lastScrollTime = performance.now();

      if (!this.isDragging && Math.abs(scrollDelta) > 0) {
        this.isScrolling = true;

        // Reset scrollStopTimeout - longer timeout for larger screens
        clearTimeout(this.scrollStopTimeout);
        const timeoutDuration = window.innerWidth >= 1920 ? 150 : 100;
        this.scrollStopTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, timeoutDuration);

        let newPos = this.targetPosition - scrollDelta * this.scrollSpeed;

        // Infinite loop logic
        if (newPos <= -this.singleSetWidth * 3) {
          const beforePos = newPos;
          newPos += this.singleSetWidth * 2;
          console.log(
            "[ProjectsSlider] Infinite loop jump: Left boundary reached 1",
            {
              before: beforePos,
              after: newPos,
              singleSetWidth: this.singleSetWidth,
            },
          );
          // Immediately sync currentPosition to prevent visual jump
          this.currentPosition = newPos;
        } else if (newPos >= -this.singleSetWidth) {
          const beforePos = newPos;
          newPos -= this.singleSetWidth * 2;
          console.log(
            "[ProjectsSlider] Infinite loop jump: Right boundary reached 1",
            {
              before: beforePos,
              after: newPos,
              singleSetWidth: this.singleSetWidth,
            },
          );
          // Immediately sync currentPosition to prevent visual jump
          this.currentPosition = newPos;
        }

        this.targetPosition = newPos;
      }
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  setupDrag() {
    let startX, startPos;
    const slider = this.slider;

    const start = (e) => {
      this.isDragging = true;
      startX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
      startPos = this.targetPosition;
      slider.style.cursor = "grabbing";
      this.hasBeenDragged = true;
    };

    const move = (e) => {
      if (!this.isDragging) return;
      const currentX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      let newPos = startPos + currentX - startX;

      // Infinite loop during drag
      if (newPos <= -this.singleSetWidth * 3) {
        const beforePos = newPos;
        newPos += this.singleSetWidth * 2;
        // console.log(
        //   "[ProjectsSlider] Infinite loop jump during drag: Left boundary reached",
        //   {
        //     before: beforePos,
        //     after: newPos,
        //     singleSetWidth: this.singleSetWidth,
        //   }
        // );
        // // Immediately sync currentPosition to prevent visual jump
        this.currentPosition = newPos;
        startPos = newPos;
        startX = currentX;
      } else if (newPos >= -this.singleSetWidth) {
        const beforePos = newPos;
        newPos -= this.singleSetWidth * 2;
        console.log(
          "[ProjectsSlider] Infinite loop jump during drag: Right boundary reached",
          {
            before: beforePos,
            after: newPos,
            singleSetWidth: this.singleSetWidth,
          },
        );
        // Immediately sync currentPosition to prevent visual jump
        this.currentPosition = newPos;
        startPos = newPos;
        startX = currentX;
      }

      this.targetPosition = newPos;
    };

    const end = () => {
      this.isDragging = false;
      slider.style.cursor = "grab";
    };

    slider.addEventListener("mousedown", start);
    slider.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("mousemove", move);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("mouseup", end);
    document.addEventListener("touchend", end);
  }

  setupResize() {
    let timeout;
    this.resizeHandler = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (!this.isInitialized) return;
        const currentPos = this.currentPosition;
        this.calculateDimensions();
        this.currentPosition = currentPos;
        this.targetPosition = currentPos;
        gsap.set(this.slider, { x: this.currentPosition });
      }, 300);
    };
    window.addEventListener("resize", this.resizeHandler);
  }

  // Smooth update loop
  update() {
    if (!this.slider) return;

    // Check if we're still scrolling based on recent scroll events
    // This helps on larger devices where scroll events may be less frequent
    const timeSinceLastScroll = performance.now() - this.lastScrollTime;
    const isRecentlyScrolling = timeSinceLastScroll < 200; // Consider scrolling if event was within 200ms

    // Determine if we should use active scroll lerp
    const isActivelyScrolling =
      this.isScrolling || isRecentlyScrolling || this.isDragging;

    // Adjust lerp based on screen size and scroll state
    // Larger screens need higher lerp for smoother motion
    const baseLerp = window.innerWidth >= 1920 ? 0.15 : this.lerpEase;
    const activeLerp = window.innerWidth >= 1920 ? 0.25 : 0.18;
    const lerp = isActivelyScrolling ? activeLerp : baseLerp;

    this.currentPosition += (this.targetPosition - this.currentPosition) * lerp;
    gsap.set(this.slider, { x: this.currentPosition, overwrite: true });
  }
}

// Initialize
if (document.querySelector(".wrg.projects-slider")) {
  new ProjectsSlider();
}
